import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserPlus, Mail, Shield, Copy, RefreshCw, Ban, Trash2,
  CheckCircle2, XCircle, Clock, AlertTriangle, Info, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available scopes an admin can grant on the sibling platform.
// Kept intentionally short — the sibling platform decides what each token means.
const SCOPE_OPTIONS = [
  { id: 'orders_read',     ar: 'قراءة الطلبات',       en: 'Read Orders' },
  { id: 'orders_write',    ar: 'إدارة الطلبات',       en: 'Manage Orders' },
  { id: 'products_read',   ar: 'قراءة المنتجات',      en: 'Read Products' },
  { id: 'products_write',  ar: 'إدارة المنتجات',      en: 'Manage Products' },
  { id: 'customers_read',  ar: 'قراءة العملاء',       en: 'Read Customers' },
  { id: 'reports_read',    ar: 'قراءة التقارير',      en: 'Read Reports' },
  { id: 'ai_chat',         ar: 'استخدام مساعد AI',    en: 'Use AI Assistant' },
  { id: 'full_access',     ar: 'صلاحية كاملة',         en: 'Full Access' },
];

const STATUS_META = {
  pending:  { color: 'bg-amber-500/15 text-amber-500 border-amber-500/30',   ar: 'قيد الانتظار', en: 'Pending',  icon: Clock },
  // iter-264d — Renamed "تم القبول → Redeemed" to
  // "تم استخدام الكود → Used" to make the invitation lifecycle explicit:
  //   • Used     = redeemed successfully (permanent, delete disabled)
  //   • Expired  = code TTL elapsed WITHOUT anyone redeeming it
  redeemed: { color: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30', ar: 'تم استخدام الكود', en: 'Used', icon: CheckCircle2 },
  revoked:  { color: 'bg-rose-500/15 text-rose-500 border-rose-500/30',      ar: 'ملغى',        en: 'Revoked',  icon: Ban },
  expired:  { color: 'bg-slate-500/15 text-slate-400 border-slate-500/30',   ar: 'منتهي',        en: 'Expired',  icon: XCircle },
};

export default function AgentInvites({ embedded = false }) {
  const { getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const headers = getAuthHeaders();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showResult, setShowResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const [form, setForm] = useState({ email: '' });

  const load = async () => {
    try {
      setLoading(true);
      const url = statusFilter
        ? `${BACKEND_URL}/api/agent-invites?status=${statusFilter}`
        : `${BACKEND_URL}/api/agent-invites`;
      const res = await axios.get(url, { headers });
      setInvites(res.data || []);
    } catch {
      toast.error(t('فشل تحميل الدعوات', 'Failed to load invites'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const submit = async () => {
    if (!form.email.trim()) {
      toast.error(t('البريد الإلكتروني مطلوب', 'Email is required'));
      return;
    }
    try {
      setCreating(true);
      const res = await axios.post(
        `${BACKEND_URL}/api/agent-invites`,
        {
          email: form.email.trim().toLowerCase(),
        },
        { headers },
      );
      setShowResult(res.data);
      setShowCreate(false);
      setForm({ email: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الإنشاء', 'Create failed'));
    } finally {
      setCreating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('تم النسخ', 'Copied'));
  };

  const revoke = async (id) => {
    if (!window.confirm(t('إلغاء هذه الدعوة نهائياً؟', 'Revoke this invite?'))) return;
    try {
      await axios.post(`${BACKEND_URL}/api/agent-invites/${id}/revoke`, {}, { headers });
      toast.success(t('تم الإلغاء', 'Revoked'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm(t('حذف السجل نهائياً؟', 'Delete permanently?'))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/agent-invites/${id}`, { headers });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const daysLeft = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    return days;
  };

  return (
    <ConditionalLayout embedded={embedded}>
      <div className={embedded ? '' : 'p-6 md:p-8 max-w-7xl mx-auto'} dir={isAr ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <UserPlus className="w-8 h-8 text-amber-500" />
              {t('دعوات الوكلاء', 'Agent Invites')}
            </h1>
            <p className="text-slate-400 mt-2 text-sm">
              {t(
                'أنشئ رمز دعوة مربوطاً ببريد إلكتروني وصلاحيات محدّدة. صالح 7 أيام. يستخدمه الوكيل من منصته الشقيقة لتفعيل حسابه تلقائياً.',
                'Create an invite code tied to an email + scopes. Valid for 7 days. The agent redeems it from their sibling platform to auto-verify.',
              )}
            </p>
          </div>
          <Button
            data-testid="btn-new-invite"
            onClick={() => setShowCreate(true)}
            className="bg-amber-600 hover:bg-amber-500 text-white gap-2"
          >
            <UserPlus className="w-4 h-4" />
            {t('دعوة جديدة', 'New Invite')}
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { id: '', ar: 'الكل', en: 'All' },
            { id: 'pending', ar: 'قيد الانتظار', en: 'Pending' },
            { id: 'redeemed', ar: 'تم استخدام الكود', en: 'Used' },
            { id: 'revoked', ar: 'ملغى', en: 'Revoked' },
            { id: 'expired', ar: 'منتهي', en: 'Expired' },
          ].map((f) => (
            <button
              key={f.id || 'all'}
              data-testid={`filter-${f.id || 'all'}`}
              onClick={() => setStatusFilter(f.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                statusFilter === f.id
                  ? 'bg-amber-500 border-amber-500 text-slate-900'
                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'
              }`}
            >
              {isAr ? f.ar : f.en}
            </button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            data-testid="btn-refresh"
            onClick={load}
            className="text-slate-400 hover:text-white gap-1 ms-auto"
          >
            <RefreshCw className="w-3 h-3" />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : invites.length === 0 ? (
          <div className="text-center py-24 text-slate-400 border border-dashed border-slate-700 rounded-xl">
            <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>{t('لا توجد دعوات بعد', 'No invites yet')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {invites.map((inv) => {
              const meta = STATUS_META[inv.status] || STATUS_META.pending;
              const Icon = meta.icon;
              const days = inv.status === 'pending' ? daysLeft(inv.expires_at) : null;
              return (
                <motion.div
                  key={inv.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`invite-${inv.id}`}
                  className="bg-slate-900 border border-slate-700 rounded-xl p-4 hover:border-slate-500 transition-colors"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-white font-medium truncate">{inv.email}</span>
                        <Badge className={`gap-1 border ${meta.color}`}>
                          <Icon className="w-3 h-3" />
                          {isAr ? meta.ar : meta.en}
                        </Badge>
                        {days !== null && (
                          <span className="text-xs text-slate-400">
                            {t(`ينتهي بعد ${days} يوم`, `${days} day${days === 1 ? '' : 's'} left`)}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <code
                          className="font-mono text-lg text-amber-400 bg-slate-950 px-3 py-1 rounded border border-slate-700 tracking-wider"
                          data-testid={`code-${inv.id}`}
                        >
                          {inv.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`btn-copy-${inv.id}`}
                          onClick={() => copy(inv.code)}
                          className="text-slate-400 hover:text-white gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          {t('نسخ', 'Copy')}
                        </Button>
                      </div>

                      {inv.scope && inv.scope.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {inv.scope.map((s) => {
                            const opt = SCOPE_OPTIONS.find((o) => o.id === s);
                            return (
                              <span
                                key={s}
                                className="text-xs px-2 py-0.5 bg-slate-800 text-slate-300 rounded-full border border-slate-700"
                              >
                                <Shield className="w-3 h-3 inline me-1" />
                                {opt ? (isAr ? opt.ar : opt.en) : s}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {inv.agent_type && (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${
                            inv.agent_type === 'invited'
                              ? 'bg-blue-500/10 text-blue-300 border-blue-500/30'
                              : 'bg-purple-500/10 text-purple-300 border-purple-500/30'
                          }`}
                          data-testid={`type-${inv.id}`}
                        >
                          {inv.agent_type === 'invited'
                            ? (isAr ? 'مضاف بدعوة (بدون CV)' : 'Invited (no CV)')
                            : (isAr ? 'مقدّم طلب (مع CV)' : 'Applied (with CV)')}
                        </span>
                      )}

                      {inv.agent_profile && (
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400 bg-slate-950/50 rounded-lg p-2 border border-slate-800">
                          {inv.agent_profile.full_name_ar && (
                            <div><span className="text-slate-500">{t('الاسم', 'Name')}:</span> <span className="text-slate-200">{inv.agent_profile.full_name_ar}</span></div>
                          )}
                          {inv.agent_profile.phone && (
                            <div><span className="text-slate-500">{t('الهاتف', 'Phone')}:</span> <span className="text-slate-200" dir="ltr">{inv.agent_profile.phone}</span></div>
                          )}
                          {inv.agent_profile.city && (
                            <div><span className="text-slate-500">{t('المدينة', 'City')}:</span> <span className="text-slate-200">{inv.agent_profile.city}</span></div>
                          )}
                          {inv.agent_profile.nationality && (
                            <div><span className="text-slate-500">{t('الجنسية', 'Nationality')}:</span> <span className="text-slate-200">{inv.agent_profile.nationality}</span></div>
                          )}
                          {inv.agent_profile.languages?.length > 0 && (
                            <div className="col-span-2"><span className="text-slate-500">{t('اللغات', 'Languages')}:</span> <span className="text-slate-200">{inv.agent_profile.languages.join('، ')}</span></div>
                          )}
                          {inv.agent_profile.skills?.length > 0 && (
                            <div className="col-span-2"><span className="text-slate-500">{t('المهارات', 'Skills')}:</span> <span className="text-slate-200">{inv.agent_profile.skills.join('، ')}</span></div>
                          )}
                          {inv.agent_profile.cv_url && (
                            <div className="col-span-2">
                              <a href={inv.agent_profile.cv_url} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">
                                {t('عرض السيرة الذاتية ↗', 'View CV ↗')}
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {inv.note && (
                        <p className="text-xs text-slate-500 mt-1 italic">&ldquo;{inv.note}&rdquo;</p>
                      )}

                      <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                        <span>{t('أُنشئ', 'Created')}: {new Date(inv.created_at).toLocaleString()}</span>
                        {inv.redeemed_at && (
                          <span className="text-emerald-400">
                            {t('استُخدم على', 'Used on')} {inv.redeemed_platform} ·{' '}
                            {new Date(inv.redeemed_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {inv.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`btn-revoke-${inv.id}`}
                          onClick={() => revoke(inv.id)}
                          className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 gap-1"
                        >
                          <Ban className="w-3 h-3" />
                          {t('إلغاء', 'Revoke')}
                        </Button>
                      )}
                      {/* iter-264d — Delete is allowed only for revoked/expired
                         invitations. USED invites are kept for auditing and
                         intentionally show a locked badge instead of a delete
                         button so the agent-code link is preserved. */}
                      {(inv.status === 'revoked' || inv.status === 'expired') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`btn-delete-${inv.id}`}
                          onClick={() => del(inv.id)}
                          className="text-slate-400 hover:text-white gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      {inv.status === 'redeemed' && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
                          title={t('سجل مستخدم — يُحفظ للتدقيق ولا يمكن حذفه', 'Used record — kept for audit, cannot be deleted')}
                          data-testid={`badge-locked-${inv.id}`}
                        >
                          <Shield className="w-3 h-3" />
                          {t('محفوظ للتدقيق', 'Kept for audit')}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white max-w-lg"
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="create-invite-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-500" />
              {t('دعوة وكيل جديد', 'Invite a New Agent')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-slate-300">{t('البريد الإلكتروني', 'Email')} *</Label>
              <Input
                data-testid="input-invite-email"
                type="email"
                placeholder="agent@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-slate-950 border-slate-700 text-white"
                dir="ltr"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t(
                  'يجب أن يسجّل الوكيل دخوله بنفس هذا البريد على منصته الشقيقة.',
                  'The agent must log in with this exact email on their sibling platform.',
                )}
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-300">
                {t(
                  'الرمز صالح لمدة 7 أيام ولا يُستخدم إلا مرة واحدة. يتحقق مركز التجار من مطابقة بريد الحساب مع بريد الدعوة قبل التفعيل. يُعتمد الوكيل تلقائياً — لا حاجة لموافقة يدوية.',
                  'The code is valid for 7 days and can be redeemed only once. The merchant platform verifies the account email matches the invite email before activation. Auto-approved — no manual step.',
                )}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCreate(false)}
              className="text-slate-300"
              data-testid="btn-cancel-invite"
            >
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={creating || !form.email.trim()}
              className="bg-amber-600 hover:bg-amber-500 text-white gap-2"
              data-testid="btn-create-invite"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {t('إنشاء الرمز', 'Generate Code')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result dialog — show code once, big and copyable */}
      <Dialog open={!!showResult} onOpenChange={() => setShowResult(null)}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white max-w-md"
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="result-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              {t('تم إنشاء الرمز', 'Invite Created')}
            </DialogTitle>
          </DialogHeader>

          {showResult && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-300">
                {t(
                  'أرسل هذا الرمز إلى:',
                  'Send this code to:',
                )}{' '}
                <span className="text-white font-semibold">{showResult.email}</span>
              </p>

              <div className="bg-slate-950 border-2 border-amber-500/50 rounded-xl p-6 text-center">
                <code
                  className="font-mono text-3xl font-bold text-amber-400 tracking-widest"
                  data-testid="result-code"
                >
                  {showResult.code}
                </code>
              </div>

              <Button
                onClick={() => copy(showResult.code)}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white gap-2"
                data-testid="btn-copy-result"
              >
                <Copy className="w-4 h-4" />
                {t('نسخ الرمز', 'Copy Code')}
              </Button>

              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-200">
                  {t(
                    'ينتهي هذا الرمز في 7 أيام. الوكيل يجب أن يسجّل الدخول بنفس البريد الإلكتروني على منصته لتفعيل الحساب.',
                    'Code expires in 7 days. The agent must log in with the same email on their platform to activate.',
                  )}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setShowResult(null)}
              className="bg-slate-800 hover:bg-slate-700 text-white"
              data-testid="btn-close-result"
            >
              {t('تم', 'Done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConditionalLayout>
  );
}

function ConditionalLayout({ embedded, children }) {
  if (embedded) return <>{children}</>;
  return <DashboardLayout>{children}</DashboardLayout>;
}
